/* RESPONSIBLE TEAM: team-conversational-knowledge */
import type IntlService from 'ember-intl/services/intl';
import type ContentSection from 'embercom/models/content-service/content-section';
import type ContentStatistic from 'embercom/models/outbound/content-statistic';
import { statisticKeys } from 'embercom/models/data/outbound/constants';
import { humanReadableObjectNames } from 'embercom/models/data/matching-system/matching-constants';
import { capitalize } from '@ember/string';

function getContentTypeName(section: ContentSection | undefined): string {
  if (!section) {
    return '';
  }

  return capitalize(humanReadableObjectNames[section.entityType]);
}

function extractStatisticValueFromStats(
  stats: ContentStatistic[],
  statName: keyof typeof statisticKeys,
): number | null {
  let statisticKey = statisticKeys[statName];
  let statistic = stats.find((stat) => stat && stat.key === statisticKey);
  return statistic?.value ?? null;
}

function calculateResolutionRateFromStats(stats: ContentStatistic[]): number | null {
  let receipts = extractStatisticValueFromStats(stats, 'receipts');
  let resolutions = extractStatisticValueFromStats(stats, 'finHelpfulAnswers');

  //if there are no receipts or resolutions, or if there are no receipts, return 0
  if (receipts === null || resolutions === null || receipts === 0) {
    return 0;
  }

  return (resolutions / receipts) * 100;
}

function getSectionData(section: ContentSection, intl: IntlService, stats: ContentStatistic[]) {
  return {
    name:
      section.relatedContent?.title ||
      intl.t('knowledge-hub.content-editor.side-by-side-review.fallback-content-source'),
    involvement: extractStatisticValueFromStats(stats, 'finStepReached'),
    resolutionRate: calculateResolutionRateFromStats(stats),
    updatedTime: section.relatedContent?.updatedAt
      ? new Date(section.relatedContent.updatedAt).getTime()
      : null,
  };
}

export function buildUsageComparison(sourceData: any, targetData: any, intl: IntlService): string {
  let sourceInvolvement = sourceData.involvement ?? 0;
  let targetInvolvement = targetData.involvement ?? 0;

  if (sourceInvolvement === targetInvolvement) {
    return intl.t('knowledge-hub.content-editor.side-by-side-review.duplicate-review.usage-equal', {
      ContentA: sourceData.name,
      ContentB: targetData.name,
    });
  }

  let contentA = sourceInvolvement > targetInvolvement ? sourceData : targetData;
  let contentB = sourceInvolvement > targetInvolvement ? targetData : sourceData;

  return intl.t(
    'knowledge-hub.content-editor.side-by-side-review.duplicate-review.usage-comparison-more',
    {
      ContentA: contentA.name,
      ContentB: contentB.name,
    },
  );
}

export function buildResolutionComparison(
  sourceData: any,
  targetData: any,
  intl: IntlService,
): string {
  let sourceResolution = sourceData.resolutionRate ?? 0;
  let targetResolution = targetData.resolutionRate ?? 0;

  if (sourceResolution === targetResolution) {
    return intl.t(
      'knowledge-hub.content-editor.side-by-side-review.duplicate-review.resolution-equal',
      {
        ContentA: sourceData.name,
        ContentB: targetData.name,
      },
    );
  }

  let contentA = sourceResolution > targetResolution ? sourceData : targetData;
  let contentB = sourceResolution > targetResolution ? targetData : sourceData;

  return intl.t(
    'knowledge-hub.content-editor.side-by-side-review.duplicate-review.resolution-comparison-higher',
    {
      ContentA: contentA.name,
      ContentB: contentB.name,
    },
  );
}

export function buildUpdateTimeComparison(
  sourceData: any,
  targetData: any,
  intl: IntlService,
): string {
  let sourceTime = sourceData.updatedTime;
  let targetTime = targetData.updatedTime;

  //By default, source data will be more recent.
  let contentA = sourceData;
  let contentB = targetData;

  if ((!sourceTime && targetTime) || (sourceTime && targetTime && sourceTime < targetTime)) {
    contentA = targetData;
    contentB = sourceData;
  }

  return intl.t(
    'knowledge-hub.content-editor.side-by-side-review.duplicate-review.update-comparison-more',
    {
      ContentA: contentA.name,
      ContentB: contentB.name,
    },
  );
}

//Initial sentence referencing both content's names and content types.
export function baseReviewReason(
  sourceSection: ContentSection,
  targetSection: ContentSection,
  intl: IntlService,
): string {
  let sourceTitle =
    sourceSection.relatedContent?.title ||
    intl.t('knowledge-hub.content-editor.side-by-side-review.fallback-content-source');
  let targetTitle =
    targetSection.relatedContent?.title ||
    intl.t('knowledge-hub.content-editor.side-by-side-review.fallback-content-target');

  let sourceType = getContentTypeName(sourceSection);
  let targetType = getContentTypeName(targetSection);

  return intl.t('knowledge-hub.content-editor.side-by-side-review.duplicate-review.reason', {
    contentNameA: sourceTitle,
    contentTypeA: sourceType,
    contentNameB: targetTitle,
    contentTypeB: targetType,
  });
}

//build review reason based on which content has higher involvement
export function buildInDepthReviewReason(
  sourceSection: ContentSection,
  targetSection: ContentSection,
  intl: IntlService,
  sourceStats: ContentStatistic[],
  targetStats: ContentStatistic[],
): string {
  if (!sourceSection || !targetSection) {
    return '';
  }

  let sourceData = getSectionData(sourceSection, intl, sourceStats);
  let targetData = getSectionData(targetSection, intl, targetStats);

  let sentences: string[] = [];

  sentences.push(buildUsageComparison(sourceData, targetData, intl));

  sentences.push(buildResolutionComparison(sourceData, targetData, intl));

  sentences.push(buildUpdateTimeComparison(sourceData, targetData, intl));

  return sentences.filter(Boolean).join(' ');
}
